import React, { useState, useEffect } from 'react';

const BOOLEAN_FEATURES = [
  'enableSearch', 'enableFiltering', 'enableSorting', 'enableComparison',
  'enableWishlist', 'enableReviews', 'enableRatings', 'enableSharing',
  'enablePrint', 'enableExport', 'enableCategories', 'enableTags',
  'enableRelatedProducts', 'enableRecentlyViewed', 'enableStockStatus',
  'enablePagination'
];

const BOOLEAN_DISPLAY = [
  'showImages', 'showDescriptions', 'showPrices', 'showRatings',
  'showAddToCart', 'showQuickView', 'hoverEffects', 'showHomeTestimonials'
];

const FEATURE_LABELS = {
  enableSearch: 'Search', enableFiltering: 'Filtering', enableSorting: 'Sorting',
  enableComparison: 'Comparison', enableWishlist: 'Wishlist', enableReviews: 'Reviews',
  enableRatings: 'Ratings', enableSharing: 'Sharing', enablePrint: 'Print',
  enableExport: 'Export', enableCategories: 'Categories', enableTags: 'Tags',
  enableRelatedProducts: 'Related Products', enableRecentlyViewed: 'Recently Viewed',
  enableStockStatus: 'Stock Status', enablePagination: 'Pagination'
};

const DISPLAY_LABELS = {
  showImages: 'Show Images', showDescriptions: 'Show Descriptions', showPrices: 'Show Prices',
  showRatings: 'Show Ratings', showAddToCart: 'Show Add to Cart', showQuickView: 'Show Quick View',
  hoverEffects: 'Hover Effects', showHomeTestimonials: 'Show Home Testimonials'
};

const LAYOUTS = ['grid', 'list'];
const IMAGE_SIZES = ['small', 'medium', 'large'];
const ANIMATIONS = ['fade', 'slide', 'zoom', 'none'];

const SettingsEditor = ({ API_BASE_URL, language }) => {
  const [features, setFeatures] = useState({});
  const [display, setDisplay] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const resp = await fetch(`${API_BASE_URL}/api/settings?lang=${language}`);
        if (resp.ok) {
          const data = await resp.json();
          setFeatures(data.features || {});
          setDisplay(data.display || {});
        }
      } catch (err) {
        console.error('Failed to load settings:', err);
        setFeedback({ type: 'error', message: 'Failed to load settings.' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [API_BASE_URL, language]);

  const toggleFeature = (key) => {
    setFeatures(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleDisplay = (key) => {
    setDisplay(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const changeDisplay = (key, value) => {
    setDisplay(prev => ({ ...prev, [key]: value }));
  };

  const changeNumeric = (key, value) => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 0) {
      setFeatures(prev => ({ ...prev, [key]: num }));
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/settings?lang=${language}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ features, display })
      });
      if (resp.ok) {
        setFeedback({ type: 'success', message: 'Settings saved successfully!' });
      } else {
        throw new Error(await resp.text());
      }
    } catch (err) {
      console.error('Failed to save settings:', err);
      setFeedback({ type: 'error', message: `Failed to save: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading settings...</div>;
  }

  return (
    <div className="settings-editor">
      <div className="settings-section-group">
        <h3>Feature Toggles</h3>
        <div className="checkbox-grid">
          {BOOLEAN_FEATURES.map(key => (
            <div key={key} className="checkbox-item">
              <label>
                <input
                  type="checkbox"
                  checked={!!features[key]}
                  onChange={() => toggleFeature(key)}
                />
                {FEATURE_LABELS[key] || key}
              </label>
            </div>
          ))}
        </div>
        <div className="form-row" style={{ marginTop: 'var(--spacing-md)' }}>
          <div className="form-group">
            <label>Items Per Page</label>
            <input
              type="number"
              className="form-control"
              style={{ width: '120px' }}
              min="1"
              max="100"
              value={features.itemsPerPage ?? 12}
              onChange={(e) => changeNumeric('itemsPerPage', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Default Sort</label>
            <select
              className="form-control"
              style={{ width: '200px' }}
              value={features.defaultSort || 'priority'}
              onChange={(e) => setFeatures(prev => ({ ...prev, defaultSort: e.target.value }))}
            >
              <option value="priority">Priority</option>
              <option value="name">Name</option>
              <option value="date">Date</option>
              <option value="category">Category</option>
            </select>
          </div>
        </div>
      </div>

      <div className="settings-section-group">
        <h3>Display Configuration</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Layout</label>
            <select
              className="form-control"
              value={display.layout || 'grid'}
              onChange={(e) => changeDisplay('layout', e.target.value)}
            >
              {LAYOUTS.map(l => (
                <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Grid Columns</label>
            <select
              className="form-control"
              value={display.gridColumns || 3}
              onChange={(e) => changeDisplay('gridColumns', parseInt(e.target.value, 10))}
            >
              {[2, 3, 4].map(n => (
                <option key={n} value={n}>{n} Columns</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Image Size</label>
            <select
              className="form-control"
              value={display.imageSize || 'medium'}
              onChange={(e) => changeDisplay('imageSize', e.target.value)}
            >
              {IMAGE_SIZES.map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Animation Type</label>
            <select
              className="form-control"
              value={display.animationType || 'fade'}
              onChange={(e) => changeDisplay('animationType', e.target.value)}
            >
              {ANIMATIONS.map(a => (
                <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="checkbox-grid" style={{ marginTop: 'var(--spacing-md)' }}>
          {BOOLEAN_DISPLAY.map(key => (
            <div key={key} className="checkbox-item">
              <label>
                <input
                  type="checkbox"
                  checked={!!display[key]}
                  onChange={() => toggleDisplay(key)}
                />
                {DISPLAY_LABELS[key] || key}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="settings-actions">
        <button
          className="btn btn-primary"
          onClick={saveSettings}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        {feedback && (
          <div className={`feedback ${feedback.type}`}>
            {feedback.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsEditor;
